import { storageRegistry } from '../services/storage.js';

export interface FolderCreationResult {
  success: boolean;
  message: string;
  folderExists?: boolean;
}

/**
 * Checks if the buildstack folder exists in the file system
 */
export const checkBuildStackFolderExists = async (storageProvider: string): Promise<boolean> => {
  try {
    const provider = storageRegistry.getProvider(storageProvider);
    if (!provider) {
      console.error(`Storage provider '${storageProvider}' not found`);
      return false;
    }

    const rootFiles = await provider.list('/');
    const buildstackFolderExists = rootFiles.some(file => 
      file.type === 'folder' && (file.name === 'buildstack' || file.name === 'buildstacks')
    );

    return buildstackFolderExists;
  } catch (err) {
    console.error('Failed to check BuildStack folder existence:', err);
    return false;
  }
};

/**
 * Creates the buildstack folder in the file system if it doesn't exist
 */
export const createBuildStackFolder = async (storageProvider: string = 'enterprise'): Promise<FolderCreationResult> => {
  try {
    const folderExists = await checkBuildStackFolderExists(storageProvider);
    if (folderExists) {
      return {
        success: true,
        message: 'BuildStack folder already exists',
        folderExists: true
      };
    }

    const authToken = typeof window !== 'undefined' ? localStorage.getItem('authToken') : '';
    const baseUrl = 'http://localhost:8000/api/v1';

    const response = await fetch(`${baseUrl}/files/create-folder`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ 
        path: '/buildstacks',
        name: 'buildstacks'
      })
    });

    if (response.ok) {
      return {
        success: true,
        message: 'BuildStack folder created successfully',
        folderExists: false
      };
    } else if (response.status === 409) {
      return {
        success: true,
        message: 'BuildStack folder already exists',
        folderExists: true
      };
    } else {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
    console.error('Failed to create BuildStack folder:', err);
    
    return {
      success: false,
      message: `Failed to create BuildStack folder: ${errorMessage}`,
      folderExists: false
    };
  }
};

/**
 * Ensures the buildstack folder exists, creating it if necessary
 */
export const ensureBuildStackFolder = async (storageProvider: string = 'enterprise'): Promise<FolderCreationResult> => {
  try {
    const folderExists = await checkBuildStackFolderExists(storageProvider);
    
    if (folderExists) {
      return {
        success: true,
        message: 'BuildStack folder is ready',
        folderExists: true
      };
    }

    return await createBuildStackFolder(storageProvider);

  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
    console.error('Failed to ensure BuildStack folder:', err);
    
    return {
      success: false,
      message: `Failed to ensure BuildStack folder: ${errorMessage}`,
      folderExists: false
    };
  }
};

/**
 * Gets the buildstack folder path for file operations
 */
export const getBuildStackFolderPath = (): string => {
  return '/buildstacks';
};

/**
 * Validates that a file path is within the buildstack folder
 */
export const isValidBuildStackPath = (filePath: string): boolean => {
  const buildstackPath = getBuildStackFolderPath();
  return filePath.startsWith(buildstackPath);
};
